import { DateType, DateTimeTypeAryType } from "../types"
import dayjs from '../../dayjs';
import { isEqual, isNil } from '../common';
import { getWindowDateShowFormat, getWindowDateTimeShowFormat, isStrDateContainHMS, isStrDateContainUTC, getCustomFormatDateByWindow } from '../index'

type DateDetailInfo = {
  value: string,
  index: number,
  length: number,
  time: string,
  timeFormat: 'HH:mm:ss' | 'HH:mm'
}

// export const defaultSplit = window?.weappUtils_default_date_split || '-/.'
export const defaultSplit = window?.weappUtils_default_date_split || '-'
export const regString = `(?:(?!0000)[0-9]{4}[-/.](?:(?:0[1-9]|1[0-2])[-/.](?:0[1-9]|1[0-9]|2[0-8])|(?:0[13-9]|1[0-2])[-/.](?:29|30)|(?:0[13578]|1[02])[-/.]31)|(?:[0-9]{2}(?:0[48]|[2468][048]|[13579][26])|(?:0[48]|[2468][048]|[13579][26])00)[-/.]02[-/.]29)(\\s(?:[01][0-9]|2[0-3])(?::[0-5][0-9]){1,2})?`
export const reg = new RegExp(regString, 'g')

/**
 * @description: 获取默认的日期校验正则
 * @param {*} props_split
 * @return {*}
 */
export const getDateTestReg = (props_split?: string) => {
  const split = props_split ? props_split : defaultSplit
  return `(?:(?!0000)[0-9]{4}[${split}](?:(?:0[1-9]|1[0-2])[${split}](?:0[1-9]|1[0-9]|2[0-8])|(?:0[13-9]|1[0-2])[${split}](?:29|30)|(?:0[13578]|1[02])[${split}]31)|(?:[0-9]{2}(?:0[48]|[2468][048]|[13579][26])|(?:0[48]|[2468][048]|[13579][26])00)[${split}]02[${split}]29)(\\s(?:[01][0-9]|2[0-3])(?::[0-5][0-9]){1,2})?`
}

/**
 * @description: 获取默认的日期校验正则
 * @param {*} props_split
 * @return {*}
 */
export const isDate = (date: any, props_split?: string) => {
  if (!date) return false
  // no.2816345 【e10】优化时区及格式转换方法，支持自定义传入分隔符号
  if (typeof date === 'string') {
    const regString = getDateTestReg(props_split)
    const reg = new RegExp(regString, 'g')
    return reg.test(date)
  } else {
    return dayjs(date).isValid()
  }
}

/**
 * @description: 根据传入的string初步判断是否含有日期数据
 * @param {string} str
 * @return {boolean} 
 */
function isStringContainDate(str: string) {
  if (typeof str !== 'string') return false
  const reg = new RegExp(`(?:(?!0000)[0-9]{4}[-/.](?:(?:0[1-9]|1[0-2])[-/.](?:0[1-9]|1[0-9]|2[0-8])|(?:0[13-9]|1[0-2])[-/.](?:29|30)|(?:0[13578]|1[02])[-/.]31)|(?:[0-9]{2}(?:0[48]|[2468][048]|[13579][26])|(?:0[48]|[2468][048]|[13579][26])00)[-/.]02[-/.]29)`, 'g')
  return reg.test(str)
}

/**
 * @description: 根据入参或出参类型来对时间进行矫正 服务器在东七区存储时间为7点，用户在东八区获取的时间应该+1小时 -> 8点
 * @param {DateType} value
 * @param {*} type
 * @return {*}
 */
function localizeDate(value: DateType, type: 'in' | 'out') {
  const customFormat = getWindowDateShowFormat()
  const isContainHMS = isStrDateContainHMS(value, type)
  const f1 = `${customFormat} HH:mm:ss`
  const f2 = `${customFormat} HH:mm`
  const f3 = customFormat
  const _customFormat = customFormat ? (isContainHMS ? (dayjs(value, f1).isValid() ? f1 : f2) : f3) : undefined
  let localeValue = value
  let changeTimeZone = false
  //注意：时间戳类型自带了时区信息，在使用dayjs的时候会自动转换，在此不需要再额外计算时间差
  if (window.ECODE && window.ECODE.locale && !isNil(window.ECODE.locale.server_timezone)) {
    const server_timezone = window.ECODE.locale.server_timezone //服务器时区
    const locale_timezone = new Date().getTimezoneOffset() //本地时区
    const diff = locale_timezone - server_timezone //本地时区-服务器时区获取差值(维度：分钟)
    //注意，new Date() 、timestamp以及dayjs()的日期格式，都自带时区格式，在使用dayjs()进行处理的时候都会自动带上时区信息，所以就不需要额外再转
    //只有string类型的数据类型需要手动转
    if (typeof value === 'string') {
      if (isContainHMS) {
        //注意在进行utc检测的时候，需要注意
        //1.传入的是否是类似2022-07-28T14:12:29+09:00这种本身就含有日期时区的string
        //2.含有用户自定义格式的格式的string
        const dateInfo = getValidDateFromCustomFormat(value, _customFormat)
        const { value: v, isCustomFormat } = dateInfo
        const testUtcDate = isCustomFormat ? dateInfo : value
        const isContainUTC = isStrDateContainUTC(testUtcDate)
        if (v.isValid()) {
          changeTimeZone = true
          if (type === 'in') {
            //服务端转客户端日期（本地化处理）
            if (isContainUTC) {
              localeValue = dayjs(localeValue)
            } else {
              if (diff !== 0) localeValue = v.subtract(diff, 'minute')
            }
          } else {
            //客户端转服务端日期（统一转到东八区时间）
            if (isContainUTC) {
              localeValue = dayjs(localeValue).tz('Asia/Shanghai')
            } else {
              if (diff !== 0) localeValue = v.add(diff, 'minute')
            }
          }
        }
      }
    }
  }
  //注意这里需要判断日期格式是否为valid，在对于'不限开始' '不限结束'的情况时，dayjs(value)会抛出invalid Date
  if (changeTimeZone) {
    return dayjs(localeValue)
  } else {
    const v = getValidDateFromCustomFormat(localeValue, _customFormat).value
    return v.isValid() ? v : localeValue
  }
}

/**
 * @description: 根据传入的日期以及自定义的日期格式，判断是标准日期格式还是自定义日期格式
 * @param {any} date
 * @return {*}
 */
export function getValidDateFromCustomFormat(value: DateType, customFormat?: string) {
  const v1 = dayjs(value)
  const v2 = dayjs(value, customFormat)
  const v3 = v2.format(customFormat)
  let v = v1
  let isCustomFormat = false
  if (typeof value === 'string') {
    if (!isNil(customFormat)) {
      if (v2.isValid()) {
        if (v3 === value) {
          v = v2
          isCustomFormat = true
        }
      }
    }
  }
  return {
    value: v,
    isCustomFormat
  }
}

/**
 * @description: 根据时区对时间进行矫正，注意若value为string类型，此方法只接受标准类型的日期格式
 * @param {DateType} value
 * @param {*} type
 * @return {*}
 */
export function getCorrectDateByLocale(value: DateType | DateType[], type: 'in' | 'out') {
  if (Array.isArray(value)) {
    if (isEqual(value, [])) {
      return []
    } else {
      const ary: DateType[] = []
      value.forEach(item => ary.push(localizeDate(item, type)))
      return ary
    }
  } else {
    return localizeDate(value, type)
  }
}

//根据type转换日期格式
function formatDateByLocaleType(date: any, type: 'timestamp' | 'Date') {
  if (type === 'timestamp') {
    date = +dayjs(localizeDate(date, 'in'))
  } else {
    date = new Date(dayjs(localizeDate(date, 'in')).format())
  }
  return date
}

//组件导出内部国际化的方法
/**
 * @description: 根据服务器时间获取本地日期
 * @param {any} date
 * @param {*} type
 * @return {*}
 */
export function getLocaleDate(date: any, type: 'string' | 'timestamp' | 'Date' = 'string') {
  // if (date === '' || date === null) return date
  if (!isDate(date)) return date
  date = localizeDate(date, 'in')
  if (type === 'string') {
    date = dayjs(date).format(getWindowDateShowFormat() || 'YYYY-MM-DD')
  } else {
    date = formatDateByLocaleType(date, type)
  }
  return date
}
/**
 * @description: 根据本地时间获取服务器日期
 * @param {any} date
 * @param {*} type
 * @return {*}
 */
export function getServerDate(date: any, type: 'string' | 'timestamp' | 'Date' = 'string') {
  // if (date === '' || date === null) return date
  if (!isDate(date)) return date
  date = localizeDate(date, 'out')
  if (type === 'string') {
    date = dayjs(date).format(getWindowDateShowFormat() || 'YYYY-MM-DD')
  } else {
    date = formatDateByLocaleType(date, type)
  }
  return date
}
/**
 * @description: 根据服务器时间获取本地日期时间
 * @param {any} date
 * @param {*} type
 * @return {*}
 */
export function getLocaleDateTime(date: any, type: 'string' | 'timestamp' | 'Date' = 'string', typeAry?: DateTimeTypeAryType[]) {
  // if (date === '' || date === null) return date
  if (!isDate(date)) return date
  date = localizeDate(date, 'in')
  if (type === 'string') {
    if (isNil(typeAry)) {
      date = dayjs(date).format(getWindowDateTimeShowFormat() || 'YYYY-MM-DD HH:mm:ss')
    } else {
      date = getCustomFormatDateByWindow(date, typeAry)
    }
  } else {
    date = formatDateByLocaleType(date, type)
  }
  return date
}
/**
 * @description: 根据本地时间获取服务器日期时间
 * @param {any} date
 * @param {*} type
 * @return {*}
 */
export function getServerDateTime(date: any, type: 'string' | 'timestamp' | 'Date' = 'string') {
  // if (date === '' || date === null) return date
  if (!isDate(date)) return date
  date = localizeDate(date, 'out')
  if (type === 'string') {
    date = dayjs(date).format('YYYY-MM-DD HH:mm:ss')
  } else {
    date = formatDateByLocaleType(date, type)
  }
  return date
}
/**
 * @description: 根据服务器时间获取本地日期时间数组
 * @param {any} date
 * @return {*}
 */
export function getLocaleDateAndTime(date: any) {
  // if (date === '' || date === null) return date
  if (!isDate(date)) return date
  date = localizeDate(date, 'in')
  return [dayjs(date).format(getWindowDateShowFormat() || 'YYYY-MM-DD'), dayjs(date).format('HH:mm:ss')]
}
/**
 * @description: 根据本地时间获取服务器日期时间数组
 * @param {any} date
 * @return {*}
 */
export function getServerDateAndTime(date: any) {
  // if (date === '' || date === null) return date
  if (!isDate(date)) return date
  date = localizeDate(date, 'out')
  return [dayjs(date).format('YYYY-MM-DD'), dayjs(date).format('HH:mm:ss')]
}


/**
 * @description: 通过正则切除长字符串中的日期/日期时间，以数组形式抛出切割后的匹配对象
 * @param {string} str
 * @return {*}
 */
export function getDateArrayFromString(str: string, props_split?: string) {
  // 2022-04-24改造
  // const DateReg = /(\d{4})[-/.](\d{1,2})[-/.](\d{2})(\s*(?:[01][0-9]|2[0-3])(?::[0-5][0-9]){1,2})?/g
  // 以上正则为切割出长字符串中的日期/日期时间 但该方法在日期/日期时间前后有数字的时候会切割不当 故进行拓展 
  // 切割日期
  // const r1 = /(?!\d)((\d{4})[-/.](\d{1,2})[-/.](\d{1,2}))(?!\d)/g
  // 切割日期时间
  // const r2 = /(?<!\d)((\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(\s*(?:[01]?[0-9]|2[0-3])(?::(0?|[1-5])[0-9]){1,2})?)(?!\d)/g
  //2022-04-28 修复正则的ios safari兼容问题 注意：ios和safari不支持先行和后行断言，必须要使用以下写法
  // const reg = new RegExp(`(?<!\\d)((?:(?!0000)[0-9]{4}([-/.])(?:(?:0?[1-9]|1[0-2])([-/.])(?:0?[1-9]|1[0-9]|2[0-8])|(?:0?[13-9]|1[0-2])([-/.])(?:29|30)|(?:0?[13578]|1[02])([-/.])31)|(?:[0-9]{2}(?:0[48]|[2468][048]|[13579][26])|(?:0[48]|[2468][048]|[13579][26])00)([-/.])0?2([-/.])29)(\\s*(?:[01][0-9]|2[0-3])(?::[0-5][0-9]){1,2})?)(?!\\d)`, 'g')
  // const reg = /(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(\s*(?:[01]?[0-9]|2[0-3])(?::(0?|[1-5])[0-9]){1,2})?/g
  // const reg = /(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(\s*(?:[01][0-9]|2[0-3])(?::[0-5][0-9]){1,2})?/g

  // 2024-01-30 新增可以自定义
  const regString = getDateTestReg(props_split)
  const reg = new RegExp(regString, 'g')
  let result = reg.exec(str)
  let ary: DateDetailInfo[] = []
  while (result) {
    if (result && result[0]) {
      const preIndex = result.index - 1
      const pre = (preIndex >= 0) ? str.substr(preIndex, 1) : ''
      const nextIndex = result.index + result[0].length
      const next = str.substr(nextIndex, 1)
      if ((/\D/.test(pre) || !pre) && (/\D/.test(next) || !next)) {
        // 2023-06-12 新增判断是否含有时分秒以及是否含有秒
        const time = result[1]?.trim()
        const timeFormatReg = /\:/g
        ary.push({
          value: result[0],
          index: result.index,
          length: result[0].length,
          time: time,
          timeFormat: (time && time.match(timeFormatReg)?.length === 2) ? 'HH:mm:ss' : 'HH:mm'
        })
      }
    }
    result = reg.exec(str)
  }
  return ary
}
/**
 * @description: 通过正则切出长字符串中的日期/日期时间，调用内部方法进行规则匹配并进行相应替换
 * @param {string} str
 * @return {*}
 */
function getDateFromString(str: string, type: 'locale' | 'server', retainTimeFormat?: boolean) {
  if (typeof str !== 'string') return str
  let newString = str
  const ary = getDateArrayFromString(str)
  ary.forEach((item) => {
    if (item.length <= 10) {
      const dateCallback = type === 'locale' ? getLocaleDate : getServerDate
      newString = newString.replace(item.value, dateCallback(item.value))
    } else {
      const dateTimeCallback = type === 'locale' ? getLocaleDateTime : getServerDateTime
      if (type === 'locale') {
        const typeAry: DateTimeTypeAryType[] = (retainTimeFormat && item.timeFormat === 'HH:mm') ? ['Y', 'M', 'D', 'H', 'm'] : ['Y', 'M', 'D', 'H', 'm', 's']
        newString = newString.replace(item.value, dateTimeCallback(item.value, 'string', typeAry))
      } else {
        newString = newString.replace(item.value, dateTimeCallback(item.value))
      }
    }
  })
  return newString
}
/**
 * @description: 根据服务器时间获取本地格式化字符串
 * @param {string} str
 * @param {boolean} retainTimeFormat
 * @return {*}
 */
export function getLocaleDateString(str: string, retainTimeFormat?: boolean) {
  if (typeof str !== 'string') return str
  // 过滤掉空字符串
  if (str === '') return str
  // 过滤掉字符不超过10的字符串 2012-12-12 为最短可以转换的字符串长度
  if (str.length < 10) return str
  // 过滤掉不含数字
  if (!(/\d/g).test(str)) return str
  // 过滤掉不含date的字符串
  if (!isStringContainDate(str)) return str
  return getDateFromString(str, 'locale', retainTimeFormat)
}
/**
 * @description: 根据本地时间获取服务器格式化字符串
 * @param {string} str
 * @return {*}
 */
export function getServerDateString(str: string) {
  if (typeof str !== 'string') return str
  // 过滤掉空字符串
  if (str === '') return str
  // 过滤掉字符不超过10的字符串 2012-12-12 为最短可以转换的字符串长度
  if (str.length < 10) return str
  // 过滤掉不含数字
  if (!(/\d/g).test(str)) return str
  // 过滤掉不含date的字符串
  if (!isStringContainDate(str)) return str
  return getDateFromString(str, 'server')
}